import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Button, Tab, Nav } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faBoxOpen,
  faLayerGroup,
  faCloudArrowDown,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";

import PopupModal from "../Common/Popup";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

import ProductsList from "./ProductsList";
import CategoriesList from "./CategoriesList";
import CategoryForm from "./CategoryForm";
import ImportFromSourceForm from "./ImportFromSourceForm";
import ExportToSheetForm from "./ExportToSheetForm";

const ProductsBlock = ({ workspaceId }) => {
  const [activeTab, setActiveTab] = useState("products");

  const [categories, setCategories] = useState([]);
  const [categoriesReload, setCategoriesReload] = useState(0);
  const [productsReload, setProductsReload] = useState(0);

  const [importShow, setImportShow] = useState(false);
  const [exportShow, setExportShow] = useState(false);
  const [categoryFormShow, setCategoryFormShow] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const refreshCategories = useCallback(() => {
    setCategoriesReload((n) => n + 1);
  }, []);

  const refreshProducts = useCallback(() => {
    setProductsReload((n) => n + 1);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await Utils._apiRequest({
          path: Enums.endpoints.products.CATEGORIES,
          type: "GET",
          query: { workspace: workspaceId },
        });
        setCategories(res.data || []);
      } catch (err) {
        console.log("Failed to load categories:", err.message);
      }
    };
    fetchCategories();
  }, [workspaceId, categoriesReload]);

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormShow(true);
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryFormShow(true);
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <div className="my-3 p-3 bg-body rounded shadow-sm feature-box products-block">
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <div className="products-block__head d-flex justify-content-between align-items-center border-bottom pb-3 mb-3 flex-wrap">
                <Nav variant="pills" className="products-block__tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="products">
                      <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                      Sản phẩm
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="categories">
                      <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                      Category
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <div className="d-flex gap-2 flex-wrap">
                  {activeTab === "products" && (
                    <>
                      <Button variant="primary" size="sm" onClick={() => setImportShow(true)}>
                        <FontAwesomeIcon className="me-2" icon={faCloudArrowDown} />
                        Import từ nguồn
                      </Button>
                      <Button variant="success" size="sm" onClick={() => setExportShow(true)}>
                        <FontAwesomeIcon className="me-2" icon={faFileExport} />
                        Export Google Sheet
                      </Button>
                    </>
                  )}
                  {activeTab === "categories" && (
                    <Button variant="primary" size="sm" onClick={handleAddCategory}>
                      <FontAwesomeIcon className="me-2" icon={faPlus} />
                      Thêm Category
                    </Button>
                  )}
                </div>
              </div>

              <Tab.Content>
                <Tab.Pane eventKey="products">
                  <ProductsList
                    workspaceId={workspaceId}
                    categories={categories}
                    reload={productsReload}
                    refresh={refreshProducts}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="categories">
                  <CategoriesList
                    workspaceId={workspaceId}
                    categories={categories}
                    reload={categoriesReload}
                    refresh={refreshCategories}
                    refreshProducts={refreshProducts}
                    onEdit={handleEditCategory}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </Col>
      </Row>

      <PopupModal
        title="Import sản phẩm từ nguồn"
        popupShow={importShow}
        setPopupShow={setImportShow}
        size="lg"
      >
        <ImportFromSourceForm
          workspaceId={workspaceId}
          categories={categories}
          setPopupShow={setImportShow}
          onImported={() => {
            refreshProducts();
            refreshCategories();
          }}
        />
      </PopupModal>

      <PopupModal
        title="Export category ra Google Sheet"
        popupShow={exportShow}
        setPopupShow={setExportShow}
        size="lg"
      >
        <ExportToSheetForm
          workspaceId={workspaceId}
          categories={categories}
          setPopupShow={setExportShow}
        />
      </PopupModal>

      <PopupModal
        title={editingCategory ? "Sửa Category" : "Thêm Category"}
        popupShow={categoryFormShow}
        setPopupShow={setCategoryFormShow}
        size="md"
      >
        <CategoryForm
          workspaceId={workspaceId}
          selectedCategory={editingCategory}
          setPopupShow={setCategoryFormShow}
          refresh={refreshCategories}
        />
      </PopupModal>
    </>
  );
};

export default ProductsBlock;
